import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Modal, Button as BootstrapButton } from 'react-bootstrap';
import { Image, ImageType, ImageSubType, LabellingMode, Annotation, ANNOTATIONS_PROPERTY_NAME } from 'redux/annotations/annotations.type';
import { ImageFiles } from 'redux/images/images.type';
import { useRedux, useReduxSelector, useReduxDispatch } from 'redux/store.hooks';

/* -------------------------------------------------------------- *\
  Animations
\* -------------------------------------------------------------- */

const skeletonAnimation = keyframes`
  from { background-color: #e2e2e2; }
  50%  { background-color: #f3f3f3; }
  to   { background-color: #e2e2e2; }
`;

/* -------------------------------------------------------------- *\
  Modal
\* -------------------------------------------------------------- */

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 1400px !important;
  }

  .modal-body {
    max-height: 75vh;
    overflow-y: auto;
    padding: 1rem 1rem 0;
  }
`;

/* -------------------------------------------------------------- *\
  Grid
\* -------------------------------------------------------------- */

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

/* -------------------------------------------------------------- *\
  Thumbnail item
\* -------------------------------------------------------------- */

const ThumbnailContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ThumbnailSkeleton = styled.div`
  width: 100%;
  padding-top: 75%;
  border-radius: 4px;
  animation: ${skeletonAnimation} 1.5s infinite;
`;

const StyledThumbnailImage = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  display: block;
`;

// SVG uses the same viewBox as the original image dimensions and
// preserveAspectRatio="xMidYMid meet" — identical behaviour to object-fit:contain —
// so damage polygons drawn in original pixel coordinates stay perfectly aligned.
const DamageOverlaySvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const ImageMetadata = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
  width: 100%;
  font-size: 0.8125rem;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const ImageTypeText = styled.span`
  font-weight: 500;
  color: #333;
`;

const DamageCountText = styled.span`
  color: #666;
  font-size: 0.8125rem;
`;

/* -------------------------------------------------------------- *\
  Footer
\* -------------------------------------------------------------- */

const ConfirmationText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: #333;
  line-height: 1.5;
  white-space: nowrap;
`;

const BoldText = styled.span`
  font-weight: 600;
`;

const Footer = styled(Modal.Footer)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1.5rem;
`;

const FooterButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
`;

/* -------------------------------------------------------------- *\
  ThumbnailItem
\* -------------------------------------------------------------- */

interface ThumbnailItemProps {
  image: Image;
  typeLabel: string;
  damageCount: number;
  annotations: Annotation[] | undefined;
  localUrl: string | undefined;
  imageWidth: number | undefined;
  imageHeight: number | undefined;
}

const ThumbnailItem: React.FC<ThumbnailItemProps> = ({
  image, typeLabel, damageCount, annotations, localUrl, imageWidth, imageHeight,
}) => {
  const [loaded, setLoaded] = useState(false);

  const hasDimensions = imageWidth != null && imageHeight != null;
  const damages = annotations?.flatMap((annotation) => annotation.damages) ?? [];

  return (
    <ThumbnailContainer>
      <ThumbnailWrapper>
        <ThumbnailSkeleton style={{ display: loaded ? 'none' : 'block' }} />
        <StyledThumbnailImage
          src={localUrl}
          alt={image.fileName}
          title={image.fileName}
          style={{ display: loaded ? 'block' : 'none' }}
          onLoad={() => setLoaded(true)}
        />
        {loaded && hasDimensions && damages.length > 0 && (
          <DamageOverlaySvg
            viewBox={`0 0 ${imageWidth} ${imageHeight}`}
            preserveAspectRatio="xMidYMid meet"
          >
            {damages.map((damage) => (
              <polygon
                key={damage.id}
                points={damage.location.mask.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="rgba(220, 38, 38, 0.2)"
                stroke="#dc2626"
                strokeWidth={Math.max(imageWidth, imageHeight) * 0.006}
                strokeLinejoin="round"
              />
            ))}
          </DamageOverlaySvg>
        )}
      </ThumbnailWrapper>
      <ImageMetadata>
        <ImageTypeText>{typeLabel}</ImageTypeText>
        <DamageCountText>
          {damageCount} {damageCount === 1 ? 'damage' : 'damages'}
        </DamageCountText>
      </ImageMetadata>
    </ThumbnailContainer>
  );
};

/* -------------------------------------------------------------- *\
  Helpers
\* -------------------------------------------------------------- */

const getImageTypeLabel = (imageType: ImageType): string => ImageType[imageType];

const getImageSubTypeLabel = (imageSubType: ImageSubType | null): string =>
  imageSubType === null ? '' : ImageSubType[imageSubType];

const getDamageCount = (annotations: Annotation[] | undefined): number => {
  if (!annotations) return 0;
  return annotations.reduce((total, annotation) => total + annotation.damages.length, 0);
};

/* -------------------------------------------------------------- *\
  Modal component
\* -------------------------------------------------------------- */

interface FinishConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  imagesForLabelling: Image[] | undefined;
  onConfirm: () => void;
  photoSeriesId: string | undefined;
  labellingMode: LabellingMode;
}

const FinishConfirmationModal: React.FC<FinishConfirmationModalProps> = ({
  show, onHide, imagesForLabelling, onConfirm, photoSeriesId, labellingMode,
}) => {
  const dispatch = useReduxDispatch();
  const { annotations: { selectors: annotationsSelectors }, images: { selectors: imagesSelectors, actions: imagesActions } } = useRedux();

  const allAnnotations = useReduxSelector((state) => annotationsSelectors.selectAnnotationsByPhotoSeriesId(state, photoSeriesId as string));
  const imageFiles = useReduxSelector(imagesSelectors.selectImageFiles) as ImageFiles;
  const annotationsPropName = ANNOTATIONS_PROPERTY_NAME[labellingMode];

  // When the modal opens, download any images not yet in Redux so we have
  // their original dimensions (width/height) for the damage overlay SVG viewBox.
  useEffect(() => {
    if (!show || !imagesForLabelling) return;
    imagesForLabelling.forEach((image) => {
      if (!imageFiles?.[image.id]) {
        dispatch(imagesActions.downloadImageFile({ imageId: image.id }));
      }
    });
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!imagesForLabelling || !photoSeriesId) return null;

  return (
    <StyledModal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton>
        <Modal.Title>Please review the annotations</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ThumbnailGrid>
          {imagesForLabelling.map((image) => {
            const imageAnnotations = allAnnotations?.[annotationsPropName]?.[image.id];
            const imageFile = imageFiles?.[image.id];
            const imageTypeLabel = getImageTypeLabel(image.imageType);
            const imageSubTypeLabel = getImageSubTypeLabel(image.imageSubType);
            const typeLabel = imageSubTypeLabel
              ? `${imageTypeLabel}: ${imageSubTypeLabel}`
              : imageTypeLabel;

            return (
              <ThumbnailItem
                key={image.id}
                image={image}
                typeLabel={typeLabel}
                damageCount={getDamageCount(imageAnnotations)}
                annotations={imageAnnotations}
                localUrl={imageFile?.localUrl}
                imageWidth={imageFile?.width}
                imageHeight={imageFile?.height}
              />
            );
          })}
        </ThumbnailGrid>
      </Modal.Body>
      <Footer>
        <ConfirmationText>
          <BoldText>After finishing, labels cannot be edited!</BoldText>
          <span>I confirm all labels are added:</span>
        </ConfirmationText>
        <FooterButtonGroup>
          <BootstrapButton variant="secondary" onClick={onHide} data-testid="finishConfirmNoBtn">
            No
          </BootstrapButton>
          <BootstrapButton
            variant="primary"
            onClick={() => { onHide(); onConfirm(); }}
            data-testid="finishConfirmYesBtn"
          >
            Yes
          </BootstrapButton>
        </FooterButtonGroup>
      </Footer>
    </StyledModal>
  );
};

export { FinishConfirmationModal };
